import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type Modifier,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  ChevronDown,
  ChevronRight,
  Copy,
  CornerDownRight,
  FileText,
  GripVertical,
  MoreHorizontal,
  RotateCcw,
  Send,
  Settings2,
  StickyNote,
  Trash2,
  Wrench,
} from 'lucide-react'
import { useCurrentUser } from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose } from '@/components/ui/dialog'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { InlineFieldTextarea } from '@/components/inline-field-group'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { cn, formatMoney, formatPercent } from '@/lib/utils'
import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import type { Quote, QuoteLine } from '../api/types'
import { ManufacturerLink } from '../catalog/links'
import { ProductDetailsModal } from '../catalog/product-details'
import { ProductImage } from '../catalog/product-image'
import { EditorModalContent } from '../editor/modal'
import { QuoteLineSpecEditor } from '../editor/spec-editor'
import { AiData } from '../ui/ai-data'
import { aiEnter, useArrivals } from '../ui/ai-motion-state'
import { formatQuoteDate } from '../ui/format'
import { chosenConfigurationItems } from '../ui/quote-lines'
import { applyServicesRequest, draftEstimate, servicesQueryOptions } from '../services/services-api'
import { shippingQueryOptions, type ShippingState } from '../shipping/shipping-api'
import {
  pricingApi,
  pricingWorksheetQueryOptions,
  type PricingLineComparison,
  type PricingOutreachRequest,
} from './pricing-api'
import { PricingLeadTime, PricingSell } from './pricing-insights'
import {
  LineActionItems,
  LineEquipmentControls,
  LineManufacturer,
  LineSource,
  type WorksheetLineActions,
} from './worksheet-equipment'
import { PlanLineDetails } from './protection-plans'
import { isPlanLine, protectionPlansQueryOptions, type QuoteProtectionPlans } from './protection-plans-api'
import {
  configurationRowId,
  familyPosition,
  installationMarkup,
  insightTones,
  isInstallationLine,
  customPriceNeeded,
  lineMinimum,
  moveTargets,
  pricingFieldChange,
  pricingNotes,
  worksheetGroups,
  worksheetLineNumbers,
  worksheetPricing,
  type PricingField,
  type WorksheetGrouping,
} from './pricing-rules'
import { getApiErrorMessage } from '@/lib/api-error'

type SaveLine = (lineId: string, changes: Record<string, unknown>) => Promise<boolean>

const fieldLabels: Record<PricingField, string> = {
  quantity: 'Qty',
  list_amount: 'List',
  discount_chain: 'Discount',
  source_net_amount: 'Standard Net',
  net_amount: 'Net',
  sell_amount: 'Sell',
  margin_percent: 'Margin',
  markup_percent: 'Markup',
  profit_amount: 'Profit',
}

const outreachLater = 'Requesting pricing from manufacturers is not available yet. Enter a negotiated Net on the line.'

/** One click-to-edit worksheet value with the demo's validation and per-field error. */
function PricingValue({
  line,
  field,
  value,
  displayValue,
  busy,
  secondary = false,
  modified = false,
  align = 'right',
  desktopPrefix,
  onSave,
}: {
  line: Pick<QuoteLine, 'id' | 'model' | 'description' | 'umrp_amount'>
  field: PricingField
  value: string
  displayValue: string
  busy: boolean
  secondary?: boolean
  modified?: boolean
  align?: 'left' | 'right'
  desktopPrefix?: string
  onSave: SaveLine
}) {
  const [error, setError] = useState<string | null>(null)
  const label = fieldLabels[field]
  const name = line.model || line.description
  const save = async (raw: string) => {
    try {
      const changes = pricingFieldChange(field, raw)
      if (field === 'sell_amount' && line.umrp_amount != null && Number(raw) < Number(line.umrp_amount)) {
        throw new Error(`Sell must be at least ${formatMoney(Number(line.umrp_amount))} (UMRP).`)
      }
      setError(null)
      if (!(await onSave(line.id, changes))) setError('Not saved. Edit to retry.')
    } catch (caught) {
      setError(getApiErrorMessage(caught, 'Check this value.'))
    }
  }
  return (
    <div className={cn('min-w-0', field === 'markup_percent' && 'grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2')}>
      <span
        className={cn(
          'text-[11px] font-medium text-muted-foreground',
          field !== 'markup_percent' && 'mb-0.5 block xl:sr-only',
        )}
      >
        {label}
      </span>
      <div className={cn('flex items-center', align === 'right' ? 'justify-end' : 'justify-start')}>
        {desktopPrefix ? (
          <span aria-hidden="true" className="hidden text-[11px] font-medium text-muted-foreground xl:inline">
            {desktopPrefix}
          </span>
        ) : null}
        <POInlineEditableField
          align={align}
          ariaLabel={`Edit ${label} for ${name}`}
          buttonClassName={cn(
            'min-w-0 px-1 font-semibold tabular-nums',
            secondary ? 'min-h-6 py-0 text-[11px] font-medium text-muted-foreground' : 'min-h-8',
            modified && 'text-red-600 dark:text-red-400',
          )}
          disabled={busy}
          displayValue={displayValue}
          inputClassName={cn('w-28', field === 'discount_chain' && 'w-32')}
          invalid={Boolean(error)}
          kind={field === 'discount_chain' ? 'text' : 'number'}
          onConfirm={(raw) => void save(raw)}
          title={`${label}: ${displayValue}; click to edit`}
          value={value}
        />
      </div>
      {error ? (
        <p className="col-span-2 mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

const worksheetGrid =
  'grid grid-cols-2 gap-x-4 gap-y-3 xl:grid-cols-[minmax(150px,2fr)_40px_minmax(86px,0.9fr)_minmax(96px,1fr)_minmax(130px,1.2fr)_minmax(110px,1.1fr)] xl:gap-x-3'

function PricingColumnHeaders() {
  return (
    <div
      aria-hidden="true"
      className={cn(worksheetGrid, 'hidden border-t bg-muted/40 px-4 py-2 text-right text-xs font-medium text-muted-foreground xl:grid')}
    >
      <span className="text-left">Product</span>
      <span>Qty</span>
      <span>MAP / Minimum</span>
      <span>Net</span>
      <span>Sell</span>
      <span>Sell Total</span>
    </div>
  )
}

/** MAP beside the minimum sell: the manufacturer's UMRP when published, else our minimum margin. */
function PriceGuidelines({ line, sell, returned }: { line: QuoteLine; sell: number; returned?: PricingLineComparison }) {
  const map = line.map_amount != null && Number(line.map_amount) > 0 ? Number(line.map_amount) : null
  const minimum = lineMinimum(line, returned)
  const below = minimum !== null && sell < minimum.amount
  return (
    <dl aria-label="Price guidelines per unit" className="space-y-1 text-right tabular-nums">
      <div title="Minimum advertised price">
        <dt className="text-[11px] text-muted-foreground">MAP</dt>
        <dd className="text-sm font-semibold">{map === null ? '-' : formatMoney(map)}</dd>
      </div>
      <div
        title={
          minimum?.kind === 'umrp'
            ? 'Manufacturer minimum resale price'
            : minimum
              ? 'Lowest sell that keeps our minimum margin on this Net'
              : undefined
        }
      >
        <dt className="text-[11px] text-muted-foreground">{minimum?.kind === 'umrp' ? 'UMRP' : 'Minimum'}</dt>
        <dd className={cn('text-sm font-semibold', below && 'text-red-600 dark:text-red-400')}>
          {minimum === null ? '-' : formatMoney(minimum.amount)}
        </dd>
        {below ? <dd className="text-[11px] font-medium text-red-600 dark:text-red-400">Below minimum</dd> : null}
      </div>
    </dl>
  )
}

function NotesDialog({ line, busy, onSave }: { line: QuoteLine; busy: boolean; onSave: SaveLine }) {
  const [draft, setDraft] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const name = line.model || line.description
  const save = async () => {
    if (draft === null) return
    setSaving(true)
    setError(null)
    const saved = await onSave(line.id, { notes: draft.trim() || null })
    setSaving(false)
    if (saved) setDraft(null)
    else setError('Notes could not be saved. Try again.')
  }
  return (
    <Dialog
      onOpenChange={(open) => {
        if (saving) return
        setDraft(open ? pricingNotes(line.notes) : null)
        setError(null)
      }}
      open={draft !== null}
    >
      <Button
        aria-label={`Notes for ${name}`}
        className="h-8 gap-1 px-1.5 text-xs text-muted-foreground"
        disabled={busy}
        onClick={() => setDraft(pricingNotes(line.notes))}
        size="sm"
        variant="ghost"
      >
        <StickyNote aria-hidden="true" />
        Notes
        {line.notes ? <span className="size-1.5 rounded-full bg-primary" /> : null}
      </Button>
      {draft !== null ? (
        <EditorModalContent
          closeDisabled={saving}
          footer={
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button disabled={saving} variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <LoadingButton loading={saving} loadingText="Saving..." onClick={() => void save()}>
                Save notes
              </LoadingButton>
            </div>
          }
          title={`Notes / ${name}`}
        >
          <InlineFieldTextarea
            aria-label="Notes"
            autoFocus
            className="min-h-40"
            disabled={saving}
            maxLength={10000}
            onChange={(event) => {
              setDraft(event.target.value)
              setError(null)
            }}
            value={draft}
          />
          {error ? (
            <p className="mt-2 text-xs text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </EditorModalContent>
      ) : null}
    </Dialog>
  )
}

export function PricingWorksheetLine({
  line,
  quote,
  shipping,
  returned,
  lineNumber,
  leading,
  busy,
  childCount = 0,
  childrenExpanded = true,
  onToggleChildren,
  onSave,
  actions,
  moves = {},
  requestFrom,
  protection,
  arriving = false,
}: {
  line: QuoteLine
  quote?: Quote
  shipping?: ShippingState
  returned?: PricingLineComparison
  lineNumber?: string
  /** The drag handle of a top line, or the space it takes on a sub-line (USA-1344). */
  leading?: ReactNode
  busy: boolean
  childCount?: number
  childrenExpanded?: boolean
  onToggleChildren?: () => void
  onSave: SaveLine
  /** The Equipment stage's line controls; without them the row prices only. */
  actions?: WorksheetLineActions
  /** Where Move up / Move down take the line in the current view. */
  moves?: { up?: number; down?: number }
  /** The manufacturer whose deviated pricing covers this line. */
  requestFrom?: string
  /** The quote's Safeware plans and their checks (USA-1162). */
  protection?: QuoteProtectionPlans
  /** The line was just added (by the assistant or an import). */
  arriving?: boolean
}) {
  const pricing = worksheetPricing(line)
  const chain = line.discount_chain?.join('/') || ''
  const quantity = Number.isFinite(Number(line.quantity)) ? Number(line.quantity).toFixed(0) : String(line.quantity)
  const discount = chain.length > 13 ? `${chain.slice(0, 12)}…` : chain || '-'
  const minimum = lineMinimum(line, returned)
  const plan = isPlanLine(line)
  // A plan reads by its coverage; its SKU sits beside the manufacturer.
  const name = plan
    ? line.description
    : line.model || line.description || (line.line_type === 'service' ? 'Service' : 'Custom item')
  const equipmentProtection = protection?.equipment.find((item) => item.line_id === line.id)
  const priceNeeded = customPriceNeeded(line)
  const value = (field: PricingField, label: string, display: string, secondary = false) => (
    <PricingValue busy={busy} displayValue={display} field={field} line={line} onSave={onSave} secondary={secondary} value={label} />
  )
  const sellField = value('sell_amount', pricing.sell.toFixed(2), formatMoney(pricing.sell))
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = () => setMenuOpen(false)
  return (
    <ProductDetailsModal line={line} model={name} productId={line.product_id} quoteId={quote?.id}>
      {(onDetails) => (
        <article
          aria-label={`${name} pricing`}
          className={cn(worksheetGrid, 'border-t px-4 py-3', line.parent_line_id && 'bg-muted/20', arriving && aiEnter)}
        >
          <div className="col-span-2 flex min-w-0 items-start gap-2 xl:col-span-1">
            {leading}
            <span className="pt-0.5 text-sm font-semibold tabular-nums text-muted-foreground">
              {line.parent_line_id ? <CornerDownRight aria-hidden="true" className="mb-1 size-3.5" /> : null}
              {lineNumber ?? line.position}
            </span>
            <button
              aria-label={`Product details for ${name}`}
              className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-default"
              disabled={!onDetails}
              onClick={onDetails}
              type="button"
            >
              <ProductImage alt={line.description} className="size-12 rounded-md border p-0.5" url={line.primary_asset?.url} />
            </button>
            <div className="min-w-0 flex-1">
              {line.parent_line_id && !plan ? (
                <p className="text-[11px] text-muted-foreground">
                  {line.line_type === 'accessory' ? 'Accessory' : line.is_warranty ? 'Warranty' : 'Sub-item'}
                </p>
              ) : null}
              <h4 className="break-words text-sm font-semibold">
                <button
                  className="text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-default disabled:no-underline"
                  disabled={!onDetails}
                  onClick={onDetails}
                  type="button"
                >
                  {name}
                </button>
              </h4>
              {priceNeeded ? (
                <p className={cn('mt-1 w-fit rounded-md border px-1.5 py-0.5 text-[11px] font-semibold', insightTones.amber)}>
                  {priceNeeded}
                </p>
              ) : null}
              {line.manufacturer || line.taxonomy_label || actions ? (
                <p className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-[11px] text-muted-foreground">
                  <LineManufacturer actions={actions} line={line} />
                  <LineSource actions={actions} line={line} />
                  {plan && line.model ? <span className="truncate">· {line.model}</span> : null}
                  {line.taxonomy_label && !plan ? (
                    <span className="truncate">
                      {line.manufacturer ? '· ' : ''}
                      {line.taxonomy_label}
                    </span>
                  ) : null}
                </p>
              ) : null}
              {plan ? null : <QuoteLineSpecEditor busy={busy} line={line} onSave={onSave} />}
              {plan ? <PlanLineDetails line={line} /> : null}
              {actions ? (
                <LineEquipmentControls
                  actions={actions}
                  line={line}
                  plans={quote?.lines.filter((item) => item.parent_line_id === line.id && isPlanLine(item))}
                  protection={equipmentProtection}
                />
              ) : null}
              <div className="mt-1 max-w-40">
                <PricingValue
                  align="left"
                  busy={busy}
                  desktopPrefix="List"
                  displayValue={formatMoney(Number(line.list_amount))}
                  field="list_amount"
                  line={line}
                  onSave={onSave}
                  secondary
                  value={Number(line.list_amount).toFixed(2)}
                />
              </div>
              {!line.parent_line_id && line.line_type === 'product' ? <PricingLeadTime line={line} quote={quote} shipping={shipping} /> : null}
              {childCount > 0 ? (
                <button
                  aria-expanded={childrenExpanded}
                  className="mt-1 flex min-h-7 items-center gap-1 rounded-md text-[11px] font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                  onClick={onToggleChildren}
                  type="button"
                >
                  {childrenExpanded ? <ChevronDown aria-hidden="true" className="size-3" /> : <ChevronRight aria-hidden="true" className="size-3" />}
                  {childCount} sub-item{childCount === 1 ? '' : 's'}
                </button>
              ) : null}
            </div>
          </div>

          <div>{value('quantity', quantity, quantity)}</div>
          <PriceGuidelines line={line} returned={returned} sell={pricing.sell} />
          <div className="space-y-0.5">
            <PricingValue
              busy={busy}
              displayValue={formatMoney(pricing.net)}
              field="net_amount"
              line={line}
              modified={Boolean(pricing.adjustment)}
              onSave={onSave}
              value={pricing.net.toFixed(2)}
            />
            <PricingValue
              busy={busy}
              displayValue={pricing.usesNet ? '=NET=' : discount}
              field="discount_chain"
              line={line}
              modified={pricing.usesNet}
              onSave={onSave}
              secondary
              value={chain}
            />
            {pricing.discountChange && !pricing.adjustment ? (
              <div
                className={cn('rounded-md border px-1.5 py-1 text-right text-[11px]', insightTones.amber)}
                title={pricing.discountChange.changed_on ? `Changed ${formatQuoteDate(pricing.discountChange.changed_on)}` : undefined}
              >
                <p className="font-semibold">Discount changed</p>
                <p className="mt-0.5 tabular-nums">
                  Was <s>{pricing.discountChange.previous_discount_chain_notation}</s>
                  {pricing.discountChange.previous_amount ? (
                    <>
                      {' · '}
                      <s>{formatMoney(Number(pricing.discountChange.previous_amount))}</s>
                    </>
                  ) : null}
                </p>
              </div>
            ) : null}
            {pricing.special && !pricing.adjustment ? (
              <AiData className="px-1.5 py-1 text-right text-[11px]" label="Manufacturer special">
                <p
                  className="font-semibold text-emerald-700 dark:text-emerald-400"
                  title={`${pricing.special.code}: special Net on base equipment; options ${formatMoney(Number(pricing.special.option_net_amount))} unchanged. Ends ${formatQuoteDate(pricing.special.ends_at.slice(0, 10))}.`}
                >
                  {pricing.special.code} special
                </p>
                <p className="mt-0.5 text-muted-foreground">
                  Was <s className="tabular-nums">{formatMoney(Number(pricing.special.regular_net_amount))}</s>
                </p>
              </AiData>
            ) : null}
          </div>
          <div className="space-y-0.5">
            {line.product_id ? (
              <PricingSell
                floor={Math.max(minimum?.amount ?? 0, 0)}
                minimum={minimum?.amount ?? null}
                line={line}
                net={pricing.net}
                onApplySell={(next) => onSave(line.id, { sell_amount: next })}
                quote={quote}
              >
                {sellField}
              </PricingSell>
            ) : (
              // Services and custom items have no market prices to compare, so they stay unhighlighted.
              <div className="rounded-md border py-0.5 pl-2 pr-8">{sellField}</div>
            )}
            <div className="pl-2 pr-8">
              {value('markup_percent', pricing.markup.toFixed(2), formatPercent(pricing.markup))}
            </div>
          </div>
          <div className="col-span-2 min-w-0 space-y-0.5 text-right xl:col-span-1">
            <p className="mb-0.5 text-[11px] font-medium text-muted-foreground xl:sr-only">Sell Total</p>
            <p className="flex min-h-8 items-center justify-end whitespace-nowrap text-sm font-semibold tabular-nums">
              {formatMoney(Number(line.totals.sell))}
            </p>
            {value('profit_amount', pricing.profit.toFixed(2), `${formatMoney(pricing.profit)} profit`, true)}
            <div className="flex items-center justify-end gap-1 pt-1">
              <NotesDialog busy={busy} line={line} onSave={onSave} />
              <PickerMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <PickerMenuTrigger asChild>
                  <Button aria-label={`Actions for ${name}`} className="size-8" size="icon" variant="ghost">
                    <MoreHorizontal aria-hidden="true" />
                  </Button>
                </PickerMenuTrigger>
                <PickerMenuContent title={name} titleOnPhoneOnly align="end">
                  <PickerMenuList>
                    <PickerMenuItem
                      disabled={!onDetails}
                      onSelect={() => {
                        closeMenu()
                        onDetails?.({ currentTarget: null } as unknown as MouseEvent<HTMLButtonElement>)
                      }}
                    >
                      <FileText />
                      Product details
                    </PickerMenuItem>
                    <PickerMenuItem disabled title={outreachLater}>
                      <RotateCcw />
                      Use Standard Net
                    </PickerMenuItem>
                    {requestFrom ? (
                      <PickerMenuItem disabled title={outreachLater}>
                        <Send />
                        Request deviated pricing from {requestFrom}
                      </PickerMenuItem>
                    ) : null}
                    <PickerMenuItem disabled>Swap to preferred alternate</PickerMenuItem>
                    <PickerMenuItem disabled>Swap to budget alternate</PickerMenuItem>
                    {actions && quote ? (
                      <LineActionItems actions={actions} line={line} moves={moves} onPick={closeMenu} />
                    ) : (
                      <>
                        <PickerMenuItem disabled>
                          <Copy />
                          Duplicate item
                        </PickerMenuItem>
                        <PickerMenuItem disabled>
                          <Trash2 />
                          Remove item
                        </PickerMenuItem>
                      </>
                    )}
                  </PickerMenuList>
                </PickerMenuContent>
              </PickerMenu>
            </div>
          </div>
        </article>
      )}
    </ProductDetailsModal>
  )
}

/**
 * A product's chosen configuration as its own sub-row (demo Equipment list): the option is part of
 * the product's price, so the row shows what was chosen and opens the product's options to change it.
 */
function ConfigurationWorksheetRow({
  parent,
  item,
  lineNumber,
  leading,
  actions,
}: {
  parent: QuoteLine
  item: ReturnType<typeof chosenConfigurationItems>[number]
  lineNumber?: string
  leading?: ReactNode
  actions?: WorksheetLineActions
}) {
  const choice = item.model ? `${item.manufacturer ?? ''} ${item.model}`.trim() : item.description
  const quantity = Number.isFinite(Number(parent.quantity)) ? Number(parent.quantity).toFixed(0) : String(parent.quantity)
  return (
    <article
      aria-label={`${item.group} configuration for ${parent.model || parent.description}`}
      className={cn(worksheetGrid, 'border-t bg-muted/20 px-4 py-3')}
      data-line-kind="configuration"
    >
      <div className="col-span-2 flex min-w-0 items-start gap-2 xl:col-span-1">
        {leading}
        <span className="pt-0.5 text-sm font-semibold tabular-nums text-muted-foreground">
          <CornerDownRight aria-hidden="true" className="mb-1 size-3.5" />
          {lineNumber}
        </span>
        {item.primary_asset?.url ? (
          <ProductImage alt="" className="size-12 shrink-0 rounded-md border p-0.5" url={item.primary_asset.url} />
        ) : (
          <span
            aria-hidden="true"
            className="grid size-12 shrink-0 place-items-center rounded-md border bg-card text-muted-foreground"
          >
            <Settings2 className="size-4" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-muted-foreground">Configuration / {item.group}</p>
          <h4 className="break-words text-sm font-semibold">{choice}</h4>
          {item.model && item.description && item.description !== choice ? (
            <p className="line-clamp-2 break-words text-[11px] text-muted-foreground">{item.description}</p>
          ) : null}
          {actions ? (
            <button
              aria-label={`Change ${item.group}`}
              className="mt-1 inline-flex min-h-7 items-center gap-1 rounded-sm text-[11px] font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              onClick={() => actions.onConfigure(parent)}
              type="button"
            >
              <Settings2 aria-hidden="true" className="size-3.5" />
              Change
            </button>
          ) : null}
        </div>
      </div>
      <div className="text-right">
        <p className="mb-0.5 text-[11px] font-medium text-muted-foreground xl:sr-only">Qty</p>
        <p className="flex min-h-8 items-center justify-end text-sm font-semibold tabular-nums">{quantity}</p>
      </div>
      <div className="col-span-2 flex min-h-8 items-start justify-end text-right text-xs text-muted-foreground xl:col-span-4">
        Included in price
      </div>
    </article>
  )
}

/** Installation labor as one row, priced in Services (demo `InstallationPricingRow`). */
function InstallationPricingRow({
  lines,
  quoteId,
  leading,
  busy,
  onSave,
}: {
  lines: QuoteLine[]
  quoteId: string
  leading?: ReactNode
  busy: boolean
  onSave: SaveLine
}) {
  const net = lines.reduce((total, line) => total + Number(line.totals.net), 0)
  const sell = lines.reduce((total, line) => total + Number(line.totals.sell), 0)
  const profit = sell - net
  const markup = net ? (profit / net) * 100 : 0
  const group = { id: 'installation', model: null, description: 'Installation', umrp_amount: null }
  const value = (field: PricingField, raw: string, display: string, secondary = false) => (
    <PricingValue busy={busy} displayValue={display} field={field} line={group} onSave={onSave} secondary={secondary} value={raw} />
  )
  return (
    <article aria-label="Installation pricing" className={cn(worksheetGrid, 'border-t px-4 py-3')}>
      <div className="col-span-2 flex min-w-0 items-start gap-2 xl:col-span-1">
        {leading}
        <span className="grid size-12 shrink-0 place-items-center rounded-md border bg-muted/40 text-muted-foreground">
          <Wrench aria-hidden="true" className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold">Installation</h4>
          <p className="text-[11px] text-muted-foreground">
            Labor for {lines.length} equipment item{lines.length === 1 ? '' : 's'}
          </p>
          <Link
            className="mt-1 inline-flex min-h-7 items-center text-[11px] font-semibold text-primary hover:underline"
            params={{ quoteId, stage: 'services' }}
            to="/quotes/$quoteId/workflow/$stage"
          >
            Edit in Services
          </Link>
        </div>
      </div>
      <div className="text-right">
        <p className="text-[11px] font-medium text-muted-foreground xl:sr-only">Qty</p>
        <p className="min-h-8 text-sm font-semibold tabular-nums">1</p>
      </div>
      <dl aria-label="Price guidelines per unit" className="space-y-1 text-right tabular-nums">
        <div>
          <dt className="text-[11px] text-muted-foreground">MAP</dt>
          <dd className="text-sm font-semibold">-</dd>
        </div>
        <div>
          <dt className="text-[11px] text-muted-foreground">Minimum</dt>
          <dd className="text-sm font-semibold">-</dd>
        </div>
      </dl>
      <div className="text-right">
        <p className="text-[11px] font-medium text-muted-foreground xl:sr-only">Net</p>
        <p className="flex min-h-8 items-center justify-end text-sm font-semibold tabular-nums">{formatMoney(net)}</p>
      </div>
      <div className="space-y-0.5">
        <div className="rounded-md border py-0.5 pl-2 pr-8">{value('sell_amount', sell.toFixed(2), formatMoney(sell))}</div>
        <div className="pl-2 pr-8">{value('markup_percent', markup.toFixed(2), formatPercent(markup))}</div>
      </div>
      <div className="col-span-2 min-w-0 space-y-0.5 text-right xl:col-span-1">
        <p className="text-[11px] font-medium text-muted-foreground xl:sr-only">Sell Total</p>
        <p className="flex min-h-8 items-center justify-end text-sm font-semibold tabular-nums">{formatMoney(sell)}</p>
        {value('profit_amount', profit.toFixed(2), `${formatMoney(profit)} profit`, true)}
      </div>
    </article>
  )
}

function DisabledRequestButton({ label, count }: { label: string; count?: number }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>
          <Button className="min-h-10 sm:min-h-8" disabled variant={count === undefined ? 'default' : 'outline'}>
            <Send aria-hidden="true" />
            {label}
            {count !== undefined ? <span className="text-muted-foreground">All {count}</span> : null}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{outreachLater}</TooltipContent>
    </Tooltip>
  )
}

function ManufacturerPricingRow({ request }: { request: PricingOutreachRequest }) {
  return (
    <>
      <header className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/40 px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h3 className="text-sm font-semibold">
            <ManufacturerLink uuid={request.manufacturer_uuid}>{request.manufacturer_name}</ManufacturerLink>
          </h3>
          <span className="text-xs text-muted-foreground">
            {request.lines.length} product{request.lines.length === 1 ? '' : 's'}
          </span>
        </div>
        <DisabledRequestButton count={request.lines.length} label="Request deviated pricing" />
      </header>
      <PricingColumnHeaders />
    </>
  )
}

function CategoryRow({ label, count }: { label: string; count: number }) {
  return (
    <>
      <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t bg-muted/40 px-4 py-3">
        <h3 className="text-sm font-semibold">{label}</h3>
        <span className="text-xs text-muted-foreground">
          {count} item{count === 1 ? '' : 's'}
        </span>
      </header>
      <PricingColumnHeaders />
    </>
  )
}

const groupingOptions: { value: WorksheetGrouping; label: string }[] = [
  { value: 'item', label: 'Item number' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'category', label: 'Category' },
]

const isGrouping = (value: string | null): value is WorksheetGrouping =>
  groupingOptions.some((option) => option.value === value)

/** The quoter's Group by choice, remembered for each user on this browser (USA-1344). */
function useWorksheetGrouping() {
  const user = useCurrentUser()
  const key = `quote-builder-worksheet-grouping:${user.data?.id ?? ''}`
  const [chosen, setChosen] = useState<Record<string, WorksheetGrouping>>({})
  const stored = window.localStorage.getItem(key)
  const grouping = chosen[key] ?? (isGrouping(stored) ? stored : 'item')
  const choose = (next: WorksheetGrouping) => {
    window.localStorage.setItem(key, next)
    setChosen((previous) => ({ ...previous, [key]: next }))
  }
  return [grouping, choose] as const
}

type SortableHandle = Pick<ReturnType<typeof useSortable>, 'attributes' | 'listeners'> & {
  /** The sortable's activator ref callback; the drag starts only from the handle. */
  activator: ReturnType<typeof useSortable>['setActivatorNodeRef']
}

function DragHandle({ attributes, listeners, activator, label }: SortableHandle & { label: string }) {
  return (
    <button
      aria-label={`Drag ${label}`}
      className="-ml-2 grid h-8 w-6 shrink-0 cursor-grab touch-none place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 active:cursor-grabbing"
      ref={activator}
      type="button"
      {...attributes}
      {...listeners}
    >
      <GripVertical aria-hidden="true" className="size-4" />
    </button>
  )
}

/** A top line with its sub-lines, dragged as one by the handle on the line's row (USA-1344). */
function SortableFamily({ id, children }: { id: string; children: (handle: SortableHandle) => ReactNode }) {
  const { attributes, listeners, setActivatorNodeRef, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <div
      className={cn('relative bg-card', isDragging && 'z-10 shadow-md')}
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      {children({ attributes, listeners, activator: setActivatorNodeRef })}
    </div>
  )
}

const verticalOnly: Modifier = ({ transform }) => ({ ...transform, x: 0 })

/**
 * A view's top lines, reordered by dragging within the group they are listed in. A drop lands the
 * line, with its sub-lines, just before the line it took the place of, or just after that line's
 * family when it moved down.
 */
function SortableLines({
  roots,
  describe,
  onMove,
  children,
}: {
  roots: QuoteLine[]
  /** "item 3, ACP RFS12TS": how announcements name a line. */
  describe: (id: UniqueIdentifier) => string
  onMove?: (line: QuoteLine, anchor: QuoteLine, side: 'before' | 'after') => void
  children: (root: QuoteLine, handle?: SortableHandle) => ReactNode
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  if (!onMove) return roots.map((root) => children(root))
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${describe(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over ? `Moving ${describe(active.id)} to ${describe(over.id)}.` : `Moving ${describe(active.id)}.`,
    onDragEnd: ({ active, over }) =>
      over && over.id !== active.id
        ? `Moved ${describe(active.id)} to ${describe(over.id)}.`
        : `Dropped ${describe(active.id)}.`,
    onDragCancel: ({ active }) => `Cancelled moving ${describe(active.id)}.`,
  }
  return (
    <DndContext
      accessibility={{ announcements }}
      collisionDetection={closestCenter}
      modifiers={[verticalOnly]}
      onDragEnd={({ active, over }) => {
        const from = roots.findIndex((line) => line.id === active.id)
        const to = roots.findIndex((line) => line.id === over?.id)
        if (from < 0 || to < 0 || from === to) return
        onMove(roots[from]!, roots[to]!, to > from ? 'after' : 'before')
      }}
      sensors={sensors}
    >
      <SortableContext items={roots.map((line) => line.id)} strategy={verticalListSortingStrategy}>
        {roots.map((root) => (
          <SortableFamily id={root.id} key={root.id}>
            {(handle) => children(root, handle)}
          </SortableFamily>
        ))}
      </SortableContext>
    </DndContext>
  )
}

/** One Margin-or-markup value for the whole quote, or the quote rebate, edited in the totals. */
function QuoteTotalField({
  label,
  value,
  displayValue,
  busy,
  onSave,
  validate,
}: {
  label: string
  value: string
  displayValue: string
  busy: boolean
  onSave: (value: string) => Promise<boolean>
  validate: (value: string) => void
}) {
  const [error, setError] = useState<string | null>(null)
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">
        <POInlineEditableField
          ariaLabel={`Edit quote ${label.toLowerCase()}`}
          buttonClassName="-ml-2 min-w-0 font-semibold tabular-nums"
          disabled={busy}
          displayValue={displayValue}
          inputClassName="w-28"
          invalid={Boolean(error)}
          kind="number"
          onConfirm={async (raw) => {
            try {
              validate(raw)
              setError(null)
              if (!(await onSave(raw.trim()))) setError('Not applied. Edit to retry.')
            } catch (caught) {
              setError(getApiErrorMessage(caught, 'Check this value.'))
            }
          }}
          value={value}
        />
        {error ? (
          <p className="mt-1 text-xs text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </dd>
    </div>
  )
}

function errorMessage(caught: unknown, saved: boolean) {
  const detail = caught instanceof QuoteBuilderApiError && typeof caught.body.detail === 'object' ? caught.body.detail : null
  if (detail?.code === 'below_umrp') {
    const minimum = formatMoney(Number(detail.minimum_sell_amount))
    return detail.rebate
      ? `The quote rebate takes a product below its ${minimum} UMRP floor. Reduce the rebate or increase its sell price.`
      : `Sell must be at least ${minimum} (UMRP). Increase the price, markup, or profit and try again.`
  }
  if (detail?.code === 'quote_pricing_driver_with_rebate_not_supported') {
    return 'Quote-wide markup is not available while the quote has a rebate. Set the rebate to zero or price each line.'
  }
  if (saved) return 'Pricing saved, but the latest totals could not be refreshed. Refresh pricing before continuing.'
  if (caught instanceof QuoteBuilderApiError && caught.status === 412) return 'The quote changed. Refresh pricing, then try your edit again.'
  return 'Pricing could not be saved. Check the value and try again.'
}

/** A queued save's outcome: the worksheet shows the error in pricing words. */
export type WorksheetSave = (key: string, persist: (current: Quote) => Promise<Quote>) => Promise<{ saved: boolean; error?: unknown }>

/**
 * The demo's pricing worksheet (`PricingTab`), shown in the Equipment stage (USA-1140). Its edits
 * go through the stage's quote edit queue (`save`), so pricing and equipment edits save in order.
 */
export function PricingWorksheet({
  active = true,
  quoteId,
  quote: latestQuote,
  saving,
  save,
  onReload,
  actions,
}: {
  active?: boolean
  quoteId: string
  quote: Quote
  /** An edit is saving; prices stay read-only until it lands. */
  saving: boolean
  save: WorksheetSave
  /** Read the latest quote (a worksheet read moved its revision). */
  onReload: () => Promise<void>
  actions?: WorksheetLineActions
}) {
  const api = useQuoteBuilderApi()
  const queryClient = useQueryClient()
  const pricing = useMemo(() => pricingApi(api), [api])
  const [error, setError] = useState<string | null>(null)
  const [collapsedLines, setCollapsedLines] = useState<Set<string>>(new Set())
  const [grouping, setGrouping] = useWorksheetGrouping()
  const busy = saving
  const worksheetOptions = pricingWorksheetQueryOptions(api, quoteId)
  const worksheet = useQuery({ ...worksheetOptions, enabled: active && !busy })
  const shipping = useQuery({ ...shippingQueryOptions(api, quoteId), enabled: active, staleTime: 30_000, retry: false })
  const arriving = useArrivals(latestQuote.lines.map((line) => line.id))
  const protection = useQuery({
    ...protectionPlansQueryOptions(api, quoteId, latestQuote.revision),
    enabled: active && !busy,
    placeholderData: (previous) => previous,
  })
  const reload = useRef(onReload)
  useEffect(() => {
    reload.current = onReload
  })

  // Opening the worksheet can apply or revert specials on a draft, which moves the revision.
  const worksheetRevision = worksheet.data?.quote_revision
  const revision = latestQuote.revision
  useEffect(() => {
    if (active && worksheetRevision !== undefined && worksheetRevision > revision) {
      void reload.current().catch(() => setError('The latest pricing could not be loaded. Refresh pricing to try again.'))
    }
  }, [active, worksheetRevision, revision])

  const run = async (key: string, operation: (value: Quote) => Promise<Quote>) => {
    setError(null)
    const result = await save(key, operation)
    if (!result.saved) setError(errorMessage(result.error, false))
    void queryClient.invalidateQueries({ queryKey: worksheetOptions.queryKey }, { cancelRefetch: false })
    return result.saved
  }

  const requests = worksheet.data?.requests ?? []
  const returnedLines = new Map(requests.flatMap((request) => request.lines.map((line) => [line.line_id, line] as const)))
  const requestNames = new Map(
    requests.flatMap((request) => request.lines.map((line) => [line.line_id, request.manufacturer_name] as const)),
  )
  const { numbers: lineNumbers, childCounts, rootCount } = worksheetLineNumbers(latestQuote.lines)
  const pricingBusy =
    busy || worksheet.isPending || Boolean(worksheet.data && worksheet.data.quote_revision > latestQuote.revision)
  const installationLines = latestQuote.lines.filter(isInstallationLine)
  // Sub-lines stay under their parent (USA-1140), in every view and when a line moves (USA-1344).
  const tree = (root: QuoteLine): QuoteLine[] => [
    root,
    ...latestQuote.lines
      .filter((line) => line.parent_line_id === root.id && !isInstallationLine(line))
      .flatMap((line) => tree(line)),
  ]
  const groups = worksheetGroups(latestQuote.lines, grouping, requests)
  // Move up / Move down pass the neighbor the line is listed beside: in its group, or under its parent.
  const peers = new Map(groups.flatMap((group) => group.roots.map((root) => [root.id, group.roots] as const)))
  const movesFor = (line: QuoteLine) =>
    moveTargets(
      latestQuote.lines,
      line,
      peers.get(line.id) ??
        latestQuote.lines.filter((item) => item.parent_line_id === line.parent_line_id && !isInstallationLine(item)),
    )
  const moveFamily = actions
    ? (line: QuoteLine, anchor: QuoteLine, side: 'before' | 'after') =>
        actions.onMove(line, familyPosition(latestQuote.lines, line, anchor, side))
    : undefined
  const describe = (id: UniqueIdentifier) => {
    const line = latestQuote.lines.find((item) => item.id === id)
    return `item ${lineNumbers.get(String(id)) ?? ''}${line ? `, ${line.model || line.description}` : ''}`
  }
  const onSave: SaveLine = (lineId, changes) =>
    run(`line:${lineId}:pricing`, (value) => pricing.updateLine(value, lineId, changes))
  // Installation is priced per equipment item in Services; one worksheet edit becomes one markup there.
  const onSaveInstallation: SaveLine = async (_lineId, changes) => {
    const markup = installationMarkup(installationLines, changes)
    if (!Number.isFinite(markup) || markup < -100) throw new Error('Markup must be at least -100%.')
    return run('installation:pricing', async (value) => {
      const preview = await queryClient.fetchQuery({ ...servicesQueryOptions(api, quoteId), staleTime: 0 })
      if (preview.revision !== value.revision) throw new QuoteBuilderApiError(412, { detail: 'stale_revision' })
      return applyServicesRequest(
        api,
        value,
        preview.estimates.map((estimate) =>
          estimate.included ? { ...draftEstimate(estimate), markup_percent: markup.toFixed(10) } : draftEstimate(estimate),
        ),
      )
    })
  }
  const hiddenLines = new Set<string>()
  for (const line of latestQuote.lines) {
    if (line.parent_line_id && (collapsedLines.has(line.parent_line_id) || hiddenLines.has(line.parent_line_id))) {
      hiddenLines.add(line.id)
    }
  }
  const toggleChildren = (lineId: string) =>
    setCollapsedLines((previous) => {
      const next = new Set(previous)
      if (next.has(lineId)) next.delete(lineId)
      else next.add(lineId)
      return next
    })
  // Rows line up under the drag handles, which only top lines carry.
  const handleSpace = moveFamily ? <span aria-hidden="true" className="-ml-2 w-6 shrink-0" /> : null
  const renderLine = (line: QuoteLine, handle?: SortableHandle) =>
    hiddenLines.has(line.id) ? null : [
      <PricingWorksheetLine
        busy={pricingBusy}
        childCount={childCounts.get(line.id)}
        childrenExpanded={!collapsedLines.has(line.id)}
        key={line.id}
        leading={handle ? <DragHandle {...handle} label={describe(line.id)} /> : handleSpace}
        line={line}
        lineNumber={lineNumbers.get(line.id)}
        moves={movesFor(line)}
        onSave={onSave}
        onToggleChildren={() => toggleChildren(line.id)}
        actions={actions}
        arriving={arriving.has(line.id)}
        protection={protection.data}
        quote={latestQuote}
        requestFrom={requestNames.get(line.id)}
        returned={returnedLines.get(line.id)}
        shipping={shipping.data}
      />,
      ...(collapsedLines.has(line.id)
        ? []
        : chosenConfigurationItems(line).map((item) => {
            const rowId = configurationRowId(line.id, item.group)
            return (
              <ConfigurationWorksheetRow
                actions={actions}
                item={item}
                key={rowId}
                leading={handleSpace}
                lineNumber={lineNumbers.get(rowId)}
                parent={line}
              />
            )
          })),
    ]
  const renderLines = (roots: QuoteLine[]) => (
    <SortableLines describe={describe} onMove={moveFamily} roots={roots}>
      {(root, handle) => tree(root).map((line) => renderLine(line, line.id === root.id ? handle : undefined))}
    </SortableLines>
  )
  const totals = latestQuote.totals
  const quoteMarkup = Number(totals.net) ? (Number(totals.profit) / Number(totals.net)) * 100 : 0
  const lineCount =
    rootCount + Number(installationLines.length > 0) + latestQuote.lines.filter((line) => line.parent_line_id).length

  return (
    <TooltipProvider>
      <section
        aria-busy={busy}
        aria-label="Pricing worksheet"
        className="min-w-0 overflow-hidden rounded-xl border bg-card"
        data-stage="pricing"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-base font-semibold">Pricing worksheet</h2>
            <span className="text-xs text-muted-foreground">
              {lineCount} line{lineCount === 1 ? '' : 's'} / {requests.length} manufacturer{requests.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {pricingBusy ? (
              <span aria-live="polite" className="text-xs font-medium text-muted-foreground">
                Updating pricing...
              </span>
            ) : null}
            <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
              <span aria-hidden="true" className="whitespace-nowrap text-xs text-muted-foreground">
                Group by
              </span>
              <ReviewChoiceGroup label="Group by" onChange={setGrouping} options={groupingOptions} value={grouping} />
            </div>
            <DisabledRequestButton label="Request all deviated pricing" />
          </div>
        </div>

        <div className="border-t bg-muted/40 px-4 py-4">
          <h3 className="mb-3 text-xs font-semibold">Quote totals</h3>
          <dl className="grid grid-cols-2 gap-x-5 gap-y-4 text-sm sm:grid-cols-5">
            <div>
              <dt className="text-xs text-muted-foreground">Net</dt>
              <dd className="mt-1 font-semibold tabular-nums">{formatMoney(Number(totals.net))}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Sell Total</dt>
              <dd className="mt-1 font-semibold tabular-nums">{formatMoney(Number(totals.sell))}</dd>
            </div>
            <QuoteTotalField
              busy={pricingBusy}
              displayValue={formatMoney(Number(latestQuote.rebate_amount))}
              label="Rebate"
              onSave={(next) => run('quote:rebate', (value) => pricing.updateRebate(value, next))}
              validate={(raw) => pricingFieldChange('sell_amount', raw)}
              value={Number(latestQuote.rebate_amount).toFixed(2)}
            />
            <div>
              <dt className="text-xs text-muted-foreground">Profit</dt>
              <dd className="mt-1 font-semibold tabular-nums">{formatMoney(Number(totals.profit))}</dd>
            </div>
            <QuoteTotalField
              busy={pricingBusy}
              displayValue={formatPercent(quoteMarkup)}
              label="Markup"
              onSave={(next) => run('quote:markup', (value) => pricing.updatePricing(value, { markup_percent: next }))}
              validate={(raw) => pricingFieldChange('markup_percent', raw)}
              value={quoteMarkup.toFixed(2)}
            />
          </dl>
        </div>

        {error || worksheet.isError ? (
          <div
            className="flex flex-wrap items-center justify-between gap-2 border-t border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
            role="alert"
          >
            <span>{error || 'Manufacturer pricing could not be loaded. Your quote lines are still available.'}</span>
            <LoadingButton
              loading={busy}
              onClick={() => {
                setError(null)
                void onReload()
                  .then(() => queryClient.invalidateQueries({ queryKey: worksheetOptions.queryKey }))
                  .catch(() => setError('The latest pricing could not be loaded. Refresh pricing to try again.'))
              }}
              variant="outline"
            >
              Refresh pricing
            </LoadingButton>
          </div>
        ) : null}
        {worksheet.isLoading ? (
          <p className="border-t px-4 py-3 text-xs text-muted-foreground" role="status">
            Loading manufacturer pricing...
          </p>
        ) : null}

        {groups.map((group) => {
          if (group.kind === 'list' || group.kind === 'other') {
            if (!group.roots.length && !installationLines.length) return null
            return (
              <div key={group.key}>
                {group.kind === 'other' ? (
                  <h3 className="border-t bg-muted/40 px-4 py-3 text-sm font-semibold">{group.label}</h3>
                ) : null}
                <PricingColumnHeaders />
                {renderLines(group.roots)}
              </div>
            )
          }
          return (
            <div className="border-t-8 border-muted/60 first:border-t" key={group.key}>
              {group.request ? (
                <ManufacturerPricingRow request={group.request} />
              ) : (
                <CategoryRow count={group.roots.length} label={group.label} />
              )}
              {renderLines(group.roots)}
            </div>
          )
        })}
        {installationLines.length ? (
          <InstallationPricingRow
            busy={pricingBusy}
            leading={handleSpace}
            lines={installationLines}
            onSave={onSaveInstallation}
            quoteId={quoteId}
          />
        ) : null}
        {!latestQuote.lines.length ? (
          <p className="border-t px-4 py-10 text-center text-sm text-muted-foreground">
            Add equipment or custom items to start pricing your quote.
          </p>
        ) : null}
      </section>
    </TooltipProvider>
  )
}

